import { FACTS } from "@aybuben/client/facts/index.ts";
import {
	launchGate,
	readingTimeMs,
	type CloseReason,
	type LoaderConfig,
} from "@aybuben/client/offline/launch.ts";
import type { LangId } from "@aybuben/core/lang.ts";
import { useEffect, useRef, useState } from "react";

import { useStrings } from "../i18n/index.tsx";
import { COL, UI } from "../theme.ts";
import { Mascot } from "./Mascot.tsx";

interface LaunchLoaderProps {
	lang: LangId;
	config: LoaderConfig;
	bootDoneAt: number | null;
	priorityDoneAt: number | null;
	onClose: (reason: CloseReason, shownMs: number) => void;
}

/**
 * The launch screen: the duduk, telling one fact about Armenia, over the app
 * while it loads and the current unit's recordings download. It asks to close
 * once, when `launchGate` allows.
 */
export function LaunchLoader({
	lang,
	config,
	bootDoneAt,
	priorityDoneAt,
	onClose,
}: LaunchLoaderProps) {
	const t = useStrings();
	const [shownAt] = useState(Date.now);
	// An index, not the text: a language switch mid-launch keeps the same fact.
	const [factIndex] = useState(() => Math.floor(Math.random() * FACTS.en.length));
	const fact = FACTS[lang][factIndex] ?? FACTS.en[0]!; // FACTS.en is non-empty (facts.test.ts)
	const readMs = readingTimeMs(fact, config);
	// Screen readers announce changes to a live region, not what it mounts
	// with, so only the fact text lands after mount; the mascot and title are there at once.
	const [filled, setFilled] = useState(false);
	useEffect(() => {
		setFilled(true);
	}, []);
	// The callback can change identity on every Root render; the timer must not restart for it.
	const close = useRef(onClose);
	const closed = useRef(false);
	useEffect(() => {
		close.current = onClose;
	});
	useEffect(() => {
		let timer: ReturnType<typeof setTimeout> | undefined;
		const decide = () => {
			if (closed.current) return;
			const now = Date.now();
			const g = launchGate({
				now,
				shownAt,
				bootDoneAt,
				priorityDoneAt,
				readMs,
				maxWaitMs: config.maxWaitMs,
			});
			if (g.close) {
				closed.current = true;
				close.current(g.reason, now - shownAt);
			} else if (g.recheckInMs !== null) timer = setTimeout(decide, g.recheckInMs);
		};
		decide();
		return () => clearTimeout(timer);
	}, [bootDoneAt, priorityDoneAt, readMs, config.maxWaitMs, shownAt]);
	return (
		<div
			data-testid="launch-loader"
			// No aria-label: some screen readers announce a live region's label
			// instead of its content, and the fact went unread. The label is hidden
			// text inside the region instead.
			role="status"
			className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 px-8 text-center"
			style={{ background: COL.bg }}
		>
			<span className="sr-only">{t("loader.label")}</span>
			<Mascot state="tell" size={180} />
			<div className="max-w-sm">
				<div className="text-sm font-extrabold" style={{ color: COL.pom }}>
					{t("loader.factTitle")}
				</div>
				<p className="mt-2 text-lg leading-relaxed" style={{ color: COL.ink, fontFamily: UI }}>
					{filled ? fact : null}
				</p>
			</div>
		</div>
	);
}
